
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Automat {
  id: string;
  name: string;
  location: string;
  status: 'aktiv' | 'wartung' | 'außer-betrieb' | 'im-lager';
  startDate: string;
  image?: string;
  salesCount: number;
  revenue: number;
  nayaxSerialNumber?: string; // NEU: Optional Nayax Seriennummer
}

export default function Automaten() {
  const { user } = useAuth();
  const [automaten, setAutomaten] = useState<Automat[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAutomat, setEditingAutomat] = useState<Automat | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    status: 'aktiv' as 'aktiv' | 'wartung' | 'außer-betrieb' | 'im-lager',
    startDate: '',
    image: null as File | null,
    nayaxSerialNumber: '' // NEU: Nayax Seriennummer hinzugefügt
  });

  // Load automaten from Supabase
  useEffect(() => {
    if (user) {
      loadAutomaten();
    }
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-menu-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAutomaten = async () => {
    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat') // KORRIGIERT: Verwendet wieder 'automat'
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('🤖 Loaded automat data:', data.length, 'items');

      // ERWEITERT: Filtere nur echte Maschinen (nicht Referenzen)
      const automatData = data
        .filter(item => {
          // Nur echte Automaten-Maschinen laden
          return item.data && item.data.machine_type === 'physical_automat';
        })
        .map(item => ({
          id: item.id,
          ...item.data
        }));

      console.log('🤖 Filtered physical automaten:', automatData.length, 'machines');
      setAutomaten(automatData);
    } catch (error) {
      console.error('Error loading automaten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const automatData = {
        machine_type: 'physical_automat', // HINZUGEFÜGT: Kennzeichnung als echte Maschine
        type: 'machine', // Behalten für Kompatibilität
        name: formData.name,
        location: formData.location,
        status: formData.status,
        startDate: formData.startDate,
        nayaxSerialNumber: formData.nayaxSerialNumber, // NEU: Nayax Seriennummer speichern
        salesCount: 0,
        revenue: 0
      };

      const { data, error } = await supabase
        .from('daten')
        .insert({
          user_id: user?.id,
          data_type: 'automat', // KORRIGIERT: Verwendet wieder erlaubten Wert
          data: automatData
        })
        .select()
        .single();

      if (error) throw error;

      setAutomaten(prev => [{
        id: data.id,
        ...automatData
      }, ...prev]);

      setIsModalOpen(false);
      setFormData({
        name: '',
        location: '',
        status: 'aktiv',
        startDate: '',
        image: null,
        nayaxSerialNumber: '' // NEU: Zurücksetzen
      });
    } catch (error) {
      console.error('Error adding automat:', error);
    }
  };

  const handleEdit = (automat: Automat) => {
    setEditingAutomat(automat);
    setFormData({
      name: automat.name,
      location: automat.location,
      status: automat.status,
      startDate: automat.startDate,
      image: null,
      nayaxSerialNumber: automat.nayaxSerialNumber || '' // NEU: Bestehende Seriennummer laden
    });
    setIsEditModalOpen(true);
    setActiveDropdown(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingAutomat) return;

    try {
      const automatData = {
        machine_type: 'physical_automat', // HINZUGEFÜGT: Kennzeichnung als echte Maschine
        type: 'machine', // Behalten für Kompatibilität
        name: formData.name,
        location: formData.location,
        status: formData.status,
        startDate: formData.startDate,
        nayaxSerialNumber: formData.nayaxSerialNumber, // NEU: Nayax Seriennummer aktualisieren
        salesCount: editingAutomat.salesCount,
        revenue: editingAutomat.revenue
      };

      const { error } = await supabase
        .from('daten')
        .update({ data: automatData })
        .eq('id', editingAutomat.id);

      if (error) throw error;

      setAutomaten(prev => prev.map(automat => 
        automat.id === editingAutomat.id 
          ? { ...automat, ...automatData }
          : automat
      ));

      setIsEditModalOpen(false);
      setEditingAutomat(null);
      setFormData({
        name: '',
        location: '',
        status: 'aktiv',
        startDate: '',
        image: null,
        nayaxSerialNumber: '' // NEU: Zurücksetzen
      });
    } catch (error) {
      console.error('Error updating automat:', error);
    }
  };

  const handleDelete = async (automat: Automat) => {
    if (!confirm(`Automat "${automat.name}" wirklich löschen?`)) return;

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', automat.id);

      if (error) throw error;

      setAutomaten(prev => prev.filter(a => a.id !== automat.id));
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error deleting automat:', error);
    }
  };

  const handleStatusChange = async (automat: Automat, newStatus: 'aktiv' | 'wartung' | 'außer-betrieb' | 'im-lager') => {
    try {
      const updatedData = {
        machine_type: 'physical_automat', // HINZUGEFÜGT: Kennzeichnung als echte Maschine
        type: 'machine', // Behalten für Kompatibilität
        ...automat,
        status: newStatus
      };

      const { error } = await supabase
        .from('daten')
        .update({ data: updatedData })
        .eq('id', automat.id);

      if (error) throw error;

      setAutomaten(prev => prev.map(a => 
        a.id === automat.id 
          ? { ...a, status: newStatus }
          : a
      ));
      setActiveDropdown(null);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const toggleDropdown = (e: React.MouseEvent, automatId: string) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === automatId ? null : automatId);
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Automaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-3 sm:px-4 lg:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Automaten-Verwaltung
                  <span className="ml-3 text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base break-words">
                  Verwalten Sie Ihre Verkaufsautomaten ({automaten.length} Automaten)
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0"
              >
                <i className="ri-add-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Neuen Automat hinzufügen
              </button>
            </div>
          </div>

          <div className="p-3 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {automaten.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-robot-line text-6xl w-16 h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Keine Automaten vorhanden
                </h3>
                <p className="text-gray-500">
                  Fügen Sie Ihren ersten Verkaufsautomaten hinzu, um zu beginnen.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {automaten.map((automat) => (
                  <div key={automat.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
                    <div className="h-48 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
                      {automat.image ? (
                        <img src={automat.image} alt={automat.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <i className="ri-robot-line text-6xl text-blue-400 mb-2"></i>
                          <p className="text-sm text-gray-500">Kein Bild verfügbar</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">{automat.name}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          automat.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                          automat.status === 'wartung' ? 'bg-yellow-100 text-yellow-800' :
                          automat.status === 'im-lager' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {automat.status === 'aktiv' ? 'Aktiv' :
                           automat.status === 'wartung' ? 'Wartung' : 
                           automat.status === 'im-lager' ? 'Im Lager' : 'Außer Betrieb'}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-map-pin-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                          {automat.location}
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-calendar-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                          Seit {new Date(automat.startDate).toLocaleDateString('de-DE')}
                        </div>
                        {/* NEU: Nayax Seriennummer anzeigen falls vorhanden */}
                        {automat.nayaxSerialNumber && (
                          <div className="flex items-center text-sm text-gray-600">
                            <i className="ri-qr-code-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                            Nayax S/N: {automat.nayaxSerialNumber}
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="secondary" 
                          className="flex-1"
                          onClick={() => handleEdit(automat)}
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Bearbeiten
                        </Button>
                        <div className="relative dropdown-menu-container">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(activeDropdown === automat.id ? null : automat.id);
                            }}
                            className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-gray-200 hover:bg-gray-300 text-gray-800 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm"
                          >
                            <i className="ri-more-line"></i>
                          </button>
                          {activeDropdown === automat.id && (
                            <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                              <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Status ändern
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(automat, 'aktiv');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 cursor-pointer ${
                                  automat.status === 'aktiv' ? 'text-green-600 bg-green-50' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center"></i>
                                <span>Aktiv</span>
                                {automat.status === 'aktiv' && <i className="ri-check-line ml-auto"></i>}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(automat, 'wartung');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 cursor-pointer ${
                                  automat.status === 'wartung' ? 'text-yellow-600 bg-yellow-50' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-tools-line w-4 h-4 flex items-center justify-center"></i>
                                <span>Wartung</span>
                                {automat.status === 'wartung' && <i className="ri-check-line ml-auto"></i>}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(automat, 'im-lager');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 cursor-pointer ${
                                  automat.status === 'im-lager' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-store-3-line w-4 h-4 flex items-center justify-center"></i>
                                <span>Im Lager</span>
                                {automat.status === 'im-lager' && <i className="ri-check-line ml-auto"></i>}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(automat, 'außer-betrieb');
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 cursor-pointer ${
                                  automat.status === 'außer-betrieb' ? 'text-red-600 bg-red-50' : 'text-gray-700'
                                }`}
                              >
                                <i className="ri-close-circle-line w-4 h-4 flex items-center justify-center"></i>
                                <span>Außer Betrieb</span>
                                {automat.status === 'außer-betrieb' && <i className="ri-check-line ml-auto"></i>}
                              </button>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Automat "${automat.name}" wirklich löschen?`)) {
                                    handleDelete(automat);
                                  }
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 cursor-pointer"
                              >
                                <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                                <span>Löschen</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for new machine */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Neuen Automat hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Automat Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="z.B. Automat A1"
            required
          />
          
          <Input
            label="Standort"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="z.B. Hauptgebäude Erdgeschoss"
            required
          />
          
          <Select
            label="Status"
            options={[
              { value: 'aktiv', label: 'Aktiv' },
              { value: 'wartung', label: 'Wartung' },
              { value: 'im-lager', label: 'Im Lager' },
              { value: 'außer-betrieb', label: 'Außer Betrieb' }
            ]}
            value={formData.status}
            onChange={(value) => handleInputChange('status', value as string)}
            required
          />
          
          <Input
            label="Startdatum"
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
          />
          
          {/* NEU: Nayax Seriennummer Feld */}
          <Input
            label="Nayax Seriennummer (optional)"
            value={formData.nayaxSerialNumber}
            onChange={(e) => handleInputChange('nayaxSerialNumber', e.target.value)}
            placeholder="z.B. NX123456789"
          />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Automat Bild
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Automat hinzufügen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsModalOpen(false)}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal for editing machine */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Automat bearbeiten"
        maxWidth="lg"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          <Input
            label="Automat Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="z.B. Automat A1"
            required
          />
          
          <Input
            label="Standort"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="z.B. Hauptgebäude Erdgeschoss"
            required
          />
          
          <Select
            label="Status"
            options={[
              { value: 'aktiv', label: 'Aktiv' },
              { value: 'wartung', label: 'Wartung' },
              { value: 'im-lager', label: 'Im Lager' },
              { value: 'außer-betrieb', label: 'Außer Betrieb' }
            ]}
            value={formData.status}
            onChange={(value) => handleInputChange('status', value as string)}
            required
          />
          
          <Input
            label="Startdatum"
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            required
          />
          
          {/* NEU: Nayax Seriennummer Feld im Bearbeiten-Modal */}
          <Input
            label="Nayax Seriennummer (optional)"
            value={formData.nayaxSerialNumber}
            onChange={(e) => handleInputChange('nayaxSerialNumber', e.target.value)}
            placeholder="z.B. NX123456789"
          />
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Neues Bild (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Speichern
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsEditModalOpen(false)}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
